import { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface LaborCasesFormProps {
  form: UseFormReturn<any>
  isView?: boolean
  officeCodes?: string[]
}

export function LaborCasesForm({ form, isView, officeCodes = [] }: LaborCasesFormProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="Office Code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Office Code *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={isView}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select office code" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {officeCodes.length > 0 ? (
                      officeCodes.map((code) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none">No office codes available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Case Title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Case Title *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter case title" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Case Number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Case Number *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter case number" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Office/Tribunal Pending"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Office/Tribunal Pending *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter office/tribunal pending" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Nature Of Offense"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nature of Offense *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter nature of offense" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="Adverse Party"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adverse Party *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter adverse party" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Address Of Adverse Party"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address of Adverse Party *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter address" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter status" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Action Taken"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Taken *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter action taken" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Labor Case Type */}
      <div className="border-t-2 border-primary/20 pt-6">
        <FormField
          control={form.control}
          name="Option"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Labor Case Type *</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isView}
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unfair Labor Practice" id="unfair" />
                    <label htmlFor="unfair" className="text-sm font-medium cursor-pointer">
                      Unfair Labor Practice
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Illegal Dismissal" id="dismissal" />
                    <label htmlFor="dismissal" className="text-sm font-medium cursor-pointer">
                      Illegal Dismissal
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Money Claims" id="money" />
                    <label htmlFor="money" className="text-sm font-medium cursor-pointer">
                      Money Claims
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Illegal Deduction" id="deduction" />
                    <label htmlFor="deduction" className="text-sm font-medium cursor-pointer">
                      Illegal Deduction
                    </label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
